import React, { useState, useEffect } from "react"
import { Col, Row, Input, Button, Select } from "antd"
import { OrderedListOutlined } from "@ant-design/icons"
import PageHeader from "../../../../components/Elements/PageHeader"

// Firebase v9+ Modular SDK imports
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore"
import { db } from "../../../../firebase/config"

export default function OrderCustomization() {
  // States for order fetching and updating process
  const [orderNumber, setOrderNumber] = useState("")
  const [order, setOrder] = useState(null)
  const [clients, setClients] = useState([]) // List of clients based on order.uid
  const [selectedClient, setSelectedClient] = useState(null)
  const [manufacturers, setManufacturers] = useState([]) // List of manufacturers
  const [selectedManufacturer, setSelectedManufacturer] = useState(null)

  // Function to fetch the order based on the provided orderNumber when button is clicked
  const fetchOrder = async () => {
    if (!orderNumber) return
    try {
      // Query the "orders" collection for a matching orderNumber
      const ordersRef = collection(db, "orders")
      const orderQuery = query(ordersRef, where("orderNumber", "==", orderNumber))
      const orderSnapshot = await getDocs(orderQuery)

      if (orderSnapshot.empty) {
        console.log("No matching orders found")
        return
      }

      // Assume the first matching document is the desired one
      const orderDoc = orderSnapshot.docs[0]
      const fetchedOrder = { id: orderDoc.id, ...orderDoc.data() }
      console.log("Fetched order:", fetchedOrder)
      setOrder(fetchedOrder)

      // Fetch clients where "user" equals the order's uid
      const clientsRef = collection(db, "clients")
      const clientsQuery = query(clientsRef, where("user", "==", fetchedOrder.uid))
      const clientsSnapshot = await getDocs(clientsQuery)

      const clientsList = []
      clientsSnapshot.forEach((doc) => {
        clientsList.push({ id: doc.id, ...doc.data() })
      })
      setClients(clientsList)
    } catch (error) {
      console.error("Error fetching order:", error)
    }
  }

  // Fetch manufacturers once when the component mounts
  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const manufacturersRef = collection(db, "manufacturers")
        const mSnapshot = await getDocs(manufacturersRef)
        const mList = []
        mSnapshot.forEach((doc) => {
          mList.push({ id: doc.id, ...doc.data() })
        })
        setManufacturers(mList)
      } catch (error) {
        console.error("Error fetching manufacturers:", error)
      }
    }

    fetchManufacturers()
  }, [])

  // Update order with selected client data
  const updateOrderClient = async () => {
    if (!order || !selectedClient) return
    try {
      const orderDocRef = doc(db, "orders", order.id)
      await updateDoc(orderDocRef, {
        client: selectedClient,
        clientId: selectedClient.id,
      })
      console.log("Order updated successfully with client information")
      setOrder({ ...order, client: selectedClient, clientId: selectedClient.id })
    } catch (error) {
      console.error("Error updating order with client:", error)
    }
  }

  // Update order with selected manufacturer data
  const updateOrderManufacturer = async () => {
    if (!order || !selectedManufacturer) return
    try {
      const orderDocRef = doc(db, "orders", order.id)
      await updateDoc(orderDocRef, {
        manufacturer: selectedManufacturer,
      })
      console.log("Order updated successfully with manufacturer information")
      setOrder({ ...order, manufacturer: selectedManufacturer })
    } catch (error) {
      console.error("Error updating order with manufacturer:", error)
    }
  }

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Order Customization" icon={<OrderedListOutlined />} />
        </Col>
        <Col span={24}>
          {/* Input for order number */}
          <Input
            placeholder="Enter Order Number"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
          />
        </Col>
        <Col span={24}>
          {/* Button to fetch the order */}
          <Button type="primary" onClick={fetchOrder}>
            Fetch Order
          </Button>
        </Col>
        {order && (
          <Col span={24}>
            <h3>Fetched Order:</h3>
            <pre>{JSON.stringify(order, null, 2)}</pre>
          </Col>
        )}
        {clients.length > 0 && (
          <Col span={24}>
            <h3>Update Client Information:</h3>
            <Select
              placeholder="Select a client"
              style={{ width: "100%" }}
              onChange={(value) => {
                const clientObj = clients.find((client) => client.id === value)
                setSelectedClient(clientObj)
              }}>
              {clients.map((client) => (
                <Select.Option key={client.id} value={client.id}>
                  {client.client} {/* adjust this field as needed */}
                </Select.Option>
              ))}
            </Select>
            {selectedClient && (
              <Button type="primary" style={{ marginTop: 10 }} onClick={updateOrderClient}>
                Update Order with Client
              </Button>
            )}
          </Col>
        )}
        {manufacturers.length > 0 && order && (
          <Col span={24}>
            <h3>Update Manufacturer Information:</h3>
            <Select
              placeholder="Select a manufacturer"
              style={{ width: "100%" }}
              onChange={(value) => {
                const manufacturerObj = manufacturers.find((m) => m.id === value)
                setSelectedManufacturer(manufacturerObj)
              }}>
              {manufacturers.map((manufacturer) => (
                <Select.Option key={manufacturer.id} value={manufacturer.id}>
                  {manufacturer.company || manufacturer.id}{" "}
                  {/* Adjust the display field as needed */}
                </Select.Option>
              ))}
            </Select>
            {selectedManufacturer && (
              <Button type="primary" style={{ marginTop: 10 }} onClick={updateOrderManufacturer}>
                Update Order with Manufacturer
              </Button>
            )}
          </Col>
        )}
      </Row>
    </div>
  )
}
